import React from 'react';
import { camelCase, startCase, memoize } from 'lodash';
import {
  ColGroup,
  CustomDynamicResultTable,
  FormattedCurrencyV2,
  NoDataContentInTable,
  SearchResultColumn,
  TableBody,
  TableHead,
  useFeatureToggle,
} from '@css-xp/base';
import './refund-view-adjustments-pane.scss';
import { Adjustments, Maybe, RefundItems, Promotion } from '@css-xp/graphtypes/src/generated-types-new';
import { SUB_BENEFIT_TYPE } from '@css-xp/base/entitlement-utils';
import { STATSIG_FEATURE_GATES, ENABLE_DESIGN_ITERATION_1 } from '@css-xp/ppc-constants';
import '../../../common/css/adjustment-item.scss';

export const emptyStateProps = {
  header: '',
  description: 'No adjustments found',
  imageUrl: '',
};

const getPromotionReasonForFlatDiscount = (adjustment: Adjustments | undefined) => {
  return (
    <div>
      {adjustment?.amount ? (
        `USD ${adjustment.amount.toFixed(2)} discount`
      ) : (
        <NoDataContentInTable testId="flat-discount-promotion-reason-no-data" />
      )}
    </div>
  );
};

const getPromotionReasonWithDescription = (
  adjustment: Maybe<Adjustments>,
  promotions: Maybe<Promotion>[],
  isFlatDiscountSupportEnabled: boolean
) => {
  const promotionForLineItem = promotions?.find((promotion) => promotion?.id === adjustment?.promotionId);
  const promotionType = promotionForLineItem?.benefits?.[0]?.subBenefitType;

  if (isFlatDiscountSupportEnabled && adjustment?.type && promotionType === SUB_BENEFIT_TYPE.FLAT) {
    return getPromotionReasonForFlatDiscount(adjustment);
  }

  return (
    <div data-testid="adjustment-invoice-note">
      {adjustment?.type && <div>{adjustment?.percent ? adjustment?.percent + '% discount' : 'N/A'}</div>}
    </div>
  );
};

const getAdjustmentValue = (
  adjustment: Maybe<Adjustments>,
  promotions: Maybe<Promotion>[],
  isFlatDiscountSupportEnabled: boolean
) => {
  const promotionForLineItem = promotions?.find((promotion) => promotion?.id === adjustment?.promotionId);
  const promotionType = promotionForLineItem?.benefits?.[0]?.subBenefitType;

  if (isFlatDiscountSupportEnabled && adjustment?.type && promotionType === SUB_BENEFIT_TYPE.FLAT) {
    return <NoDataContentInTable testId="flat-discount-no-data-ppc-refunds" />;
  }

  return (
    <div data-testid="adjustment-invoice-note">
      {adjustment?.type && <div>{adjustment?.percent ? adjustment?.percent + '% discount' : 'N/A'}</div>}
    </div>
  );
};

const getColumns = memoize((isDesignIteration1Enabled: boolean): SearchResultColumn[] => {
  if (isDesignIteration1Enabled) {
    return [];
  }

  const columns = [
    {
      name: 'Promotion Type',
      customClassNames: 'col-30',
    },
    {
      name: 'Discount',
      customClassNames: 'col-20',
    },
    {
      name: 'Adjustments',
      customClassNames: 'col-20',
    },
    {
      name: 'Reason',
      customClassNames: 'col-20',
    },
    {
      name: 'Notes',
      customClassNames: 'col-30',
    },
    {
      name: '',
      customClassNames: 'col-2',
    },
  ];

  return columns;
});

const RefundViewAdjustmentPane = (refundItem: RefundItems): JSX.Element | null => {
  const hasAdjustments = refundItem?.adjustmentDetails?.filter((adjustment) => adjustment?.type) ?? [];
  const promotions = refundItem?.promotions ?? [];
  const [isEnabled] = useFeatureToggle();
  const isFlatDiscountSupportEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_FLAT_DISCOUNT_SUPPORT);
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  const renderDiscountValue = (adjustment: Maybe<Maybe<Adjustments>>) => {
    return <>{getAdjustmentValue(adjustment, promotions, isFlatDiscountSupportEnabled)}</>;
  };
  const renderPromotionType = (adjustment: Maybe<Maybe<Adjustments>>) => {
    return (
      <>
        {adjustment?.type ? (
          startCase(camelCase(adjustment?.type))
        ) : (
          <NoDataContentInTable testId="promotion-type-no-data" />
        )}
      </>
    );
  };
  const renderAdjustmentValue = (adjustment: Maybe<Maybe<Adjustments>>, index: number) => {
    return (
      <>
        {/* // FIXME :: Handle the case when multiple promotions can be added and different discounts will be there */}
        <FormattedCurrencyV2
          key={`${adjustment?.amount ?? 0}-${index}`}
          value={adjustment?.amount ?? 0}
          currency={refundItem.currency}
          showCurrencyCode
        />
      </>
    );
  };
  const renderReasonValue = (adjustment: Maybe<Maybe<Adjustments>>) => {
    return (
      <>
        {adjustment?.reason ? (
          startCase(camelCase(adjustment?.reason))
        ) : (
          <NoDataContentInTable testId="promotion-reason-no-data" />
        )}
      </>
    );
  };
  const renderNotesValue = (adjustment: Maybe<Maybe<Adjustments>>) => {
    return <>{getPromotionReasonWithDescription(adjustment, promotions, isFlatDiscountSupportEnabled)}</>;
  };

  return (
    <div className="adjustments-line-items">
      {isDesignIteration1Enabled || <h2 className="ent-adjustments-sidebar">{refundItem?.entitlementNumber}</h2>}
      <CustomDynamicResultTable
        heading={isDesignIteration1Enabled ? '' : 'Adjustment details'}
        emptyStateProps={emptyStateProps}
        noResults={hasAdjustments.length <= 0}
        isLoading={false}
        error={''}
        testId={'refund-view-adjustments-table'}
      >
        <ColGroup columns={getColumns(isDesignIteration1Enabled)} />
        <TableHead columns={getColumns(isDesignIteration1Enabled)} />
        <TableBody>
          {refundItem?.adjustmentDetails?.map((adjustment, index) => (
            <React.Fragment key={index}>
              {isDesignIteration1Enabled ? (
                <tr className="adjustment-item-v2">
                  <td className="wrap-text-line">
                    <ul>
                      <li>Discount &#x2014; {renderDiscountValue(adjustment)}</li>
                      <li>Promotion type &#x2014; {renderPromotionType(adjustment)}</li>
                      <li>Adjustment &#x2014; {renderAdjustmentValue(adjustment, index)}</li>
                      <li>Reason &#x2014; {renderReasonValue(adjustment)}</li>
                      <li>Notes &#x2014; {renderNotesValue(adjustment)}</li>
                    </ul>
                  </td>
                </tr>
              ) : (
                <tr className="adjustment-item line-item">
                  {/* Promotion Type */}
                  <td className="wrap-text-line" data-testId="promotion-type">
                    {renderPromotionType(adjustment)}
                  </td>
                  {/* Discount */}
                  <td>{renderDiscountValue(adjustment)}</td>
                  {/* Adjustments */}
                  <td>{renderAdjustmentValue(adjustment, index)}</td>
                  {/* Reason */}
                  <td className="wrap-text-line" data-testId="promotion-reason">
                    {renderReasonValue(adjustment)}
                  </td>
                  {/* Notes */}
                  <td>{renderNotesValue(adjustment)}</td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </TableBody>
      </CustomDynamicResultTable>
    </div>
  );
};

export default React.memo(RefundViewAdjustmentPane);
